import { JsonRule, RuleEvent } from 'json-rules-engine'
import {
  EngagementTemplateField,
  PropertyValues,
  QuestionMap,
} from '../clientModels'
import { perf } from '../services/performance'
import { chain } from '../services/promiseHelpers'
import { buildRules } from '../services/rules/buildRules'
import { createRulesEventHandler } from '../services/rules/createRulesEventHandler'
import {
  createEngines,
  getEngineByEngagementTemplateId,
} from '../services/rules/engines'
import store, { setIsDelayingUiUpdates, TsaThunkAction } from '../store'
import { rulesEventHandlers } from './rulesEventsHandlers'
import {
  runQuestionRulesInternal,
  RuntimeFacts,
  selectEngagement,
  selectEngagementTemplate,
} from './rulesThunksInternal'

let isRunningRules = 0

/** Returns a boolean value indicating whether or not the rules engine is running. */
export function getIsRunningRules() {
  return isRunningRules > 0
}

// Callbacks queued by code that wants to run *after* the rules engine has
// fully unwound. updateEngagementQuestionStateSummary uses this to defer its
// engagement autosave: saving during a rules run would re-enter the engine
// (the cascade we fixed), but skipping the save entirely leaves server-side
// counts stale and the dashboard percentage out of sync with the engagement
// view. Queue here, flush on the transition from isRunningRules > 0 to 0.
const postRulesCallbacks: Array<() => void> = []

/**
 * Register a callback to fire once the rules engine has fully finished
 * (i.e. isRunningRules transitions to 0). If rules are not currently
 * running, the callback fires immediately.
 */
export function runAfterRules(callback: () => void): void {
  if (!getIsRunningRules()) {
    callback()
    return
  }
  postRulesCallbacks.push(callback)
}

/** Sets the flags indicating that the rules engine is currently running. */
function setIsRunningRules(val: boolean): void {
  // If this method is called with 'true' 2 or more times, we need to ensure
  //  that the number of calls with 'false' are equal before the condition is *actually* true.
  if (val) {
    isRunningRules++
  } else {
    isRunningRules--
  }

  // Set the dispatcher to suspend or resume UI updates, based
  //  on whether or not rules are just starting to run or just ending.
  if (val && isRunningRules === 1) {
    setIsDelayingUiUpdates(true)
  } else if (!val && isRunningRules < 1) {
    // Defer the UI-batching release to the next microtask. Multiple
    // engine.run() cycles happen back-to-back during runRulesOnNewAnswers
    // (one runQuestionRules per changed answer + a runEngagementLevelRules);
    // each one toggles isRunningRules 0 -> 1 -> 0. Without this defer,
    // every transition to 0 immediately lifts isDelayingUiUpdates and lets
    // React render the in-flight intermediate count snapshot (the
    // {54/54} state we captured in diagnostic logging). By deferring to
    // a microtask, if the next cycle starts synchronously after, the
    // counter will be > 0 when the deferred callback runs and we skip
    // the release entirely -- React only sees the final settled state.
    Promise.resolve().then(() => {
      if (isRunningRules < 1) {
        setIsDelayingUiUpdates(false)
        while (postRulesCallbacks.length > 0) {
          const cb = postRulesCallbacks.shift()
          if (cb) {
            try { cb() } catch (e) { /* swallow per-callback to keep draining */ }
          }
        }
      }
    })
  }
}

const performanceQuestionRules = perf('Rules (Question)', true)
const performanceAllRules = perf('Rules (All)', true)

/**
 * Run engagement rules that are not associated with a particular question.
 * e.g.) Engagement phase rules
 * @param engagementId
 */
export const runEngagementLevelRules = (
  engagementId: number
): TsaThunkAction<Promise<RuleEvent[]>> => async (_, getState) => {
  const state = getState()
  const engagement = selectEngagement(state, engagementId)
  const engagementTemplateId = engagement.engagementTemplateId
  const templateEngine = getEngineByEngagementTemplateId(engagementTemplateId)
  const facts: RuntimeFacts = {
    clientId: engagement.clientId,
    engagementId,
    engagementTemplateId,
    rowIndex: -1,
  }

  if (templateEngine) {
    setIsRunningRules(true)
    // Switched this to await rather than just returning the Promise from run()
    // If we don't use await other thunks don't seem to wait until the promise is resolved before continuing.
    return templateEngine.run(facts).then(x => { setIsRunningRules(false); return x; })
  }

  return Promise.resolve([])
}

let uniqueId = 0
/**
 * Run all rules for the specified engagement.
 */
export const runAllEngagementRules = (engagementId: number): TsaThunkAction => (
  dispatch,
  getState
) => {
  setIsRunningRules(true)
  const state = getState()
  const engagement = selectEngagement(state, engagementId)
  const template = selectEngagementTemplate(
    state,
    engagement.engagementTemplateId
  )
  const id = uniqueId++

  performanceAllRules.beginMark(id)

  // Run rules for each question in the engagement followed by engagement level rules
  return chain(template.questions || [], questionId =>
    runQuestionRulesInternal(getState, engagementId, questionId)
  )
    .then(() => dispatch(runEngagementLevelRules(engagementId)))
    .then(() => performanceAllRules.endMark(id))
    .then(() => setIsRunningRules(false))
}

/**
 * Run all rules for the specified question on the specified engagement.
 * @param engagmentId The engagement ID.
 * @param questionId The question ID.
 * @param rowIndexes Property values that were changed if this was the result of a partial answer update.
 */
export const runQuestionRules = (
  engagementId: number | string,
  questionId: number | string,
  properties?: PropertyValues
): TsaThunkAction => (_, getState) => {
  setIsRunningRules(true)
  performanceQuestionRules.beginMark(questionId)
  return runQuestionRulesInternal(
    getState,
    engagementId,
    questionId,
    properties
  ).then(() => performanceQuestionRules.endMark(questionId))
    .then(() => setIsRunningRules(false))
}

const rulesEventHandler = createRulesEventHandler(
  store.dispatch,
  rulesEventHandlers
)

export function loadEngagementTemplateRules(
  engagementTemplateId: number,
  rules: JsonRule[],
  questions: QuestionMap,
  fields: EngagementTemplateField[]
) {
  const includesGeneratedRules = rules.some(
    r => r.event.params.engagementTemplateFieldId
  )
  if (!includesGeneratedRules) {
    // Add client-side generated rules to the ones contained in the engagement template
    rules = rules.concat(buildRules(fields))
  }
  createEngines(engagementTemplateId, rules, rulesEventHandler, questions)
}
